import { Checkbox, FormControlLabel, List, ListItem, ListItemText, Paper, Popover, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import clsx from "clsx";
import _ from "lodash";
import moment from "moment-timezone";
import React, { useState, MouseEvent, useMemo } from "react";
import { t } from "ttag";
import Tooltip from "../../components/Tooltip/LightTooltip";
import { Add, Refresh as SvgRefresh } from "../../icons/";
import { useStoreState } from "../../models/RootStore";
import styles from "./UnitStatsTable.module.css";
import useStyles from "./UnitStatsTable.style";
import { EditIcon } from "../../logos";
import EditParamPopUp from "./EditParamPopUp";
import { IconButton } from '@coolremote/shared';

export default function UnitStatsTable(props: any) {
  const {
    isLoading,
    onRefresh,
    updateTime,
    autoupdate,
    setAutoupdate,
    paramsTable,
    showHideAllParams,
    showHideParam,
    timezone,
    updateParamRow,
    disableUpdateBtn,
    unitParamTemplate,
    bacnetUnit,
    paramsTitle = t`BACnet Device Parameters`,
    typeTitle = t`BACnet Device`,
    // Optional override so a caller (e.g. modbus) can render its own edit
    // dialog instead of the BACnet-specific EditParamPopUp below — that popup
    // reads unitParamTemplate.data keyed by shortId and writes via the BACnet
    // object-value API, neither of which modbus rows/registers can use.
    renderEditDialog,
  } = props;

  const classes = useStyles();
  const { dateFormat, timeFormat } = useStoreState((state) => state.users);
  const dateTime = `${dateFormat} ${timeFormat}`;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [editedRow, setEditedRow] = useState<any>(null);

  const closeDialog = () => {
    setEditedRow(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const editParam = (row: any) => {
    if (row.editable) {
      setEditedRow(row);
    }
  };

  const numOfParams = Object.values(paramsTable)?.filter((param: any) => !!param)?.length;
  const numOfShowing: any = Object.values(paramsTable).filter((row: any) => row?.isShowing).length;
  const allShowed = numOfParams === numOfShowing;
  const someShowed =  !allShowed && numOfShowing !== 0;

  const openParamsList = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget || null);
  };

  const formatValue = (val: any) => {
    return typeof val === 'number'
      ? Number.isInteger(val)
        ? val
        : val.toFixed(1)
      : val;
  };    
  
  // Memoize sorted and filtered params
  const orderedParams = useMemo(
    () => _.orderBy(Object.values(paramsTable), ["name"], ["asc"]),
    [paramsTable]
  );
  const showingParams = useMemo(
    () => orderedParams.filter((row: any) => row?.isShowing),
    [orderedParams]
  );
  
  const getCustomEnumValue = (row: any, unitParamTemplate: any) => {
    // Find the parameter definition by code or shortId
    const paramDef =
      unitParamTemplate?.data?.[row.code] ||
      Object.values(unitParamTemplate?.data || {}).find(
        (item: any) => item.shortId === row.code?.split('/')?.[0]
      );
    if (!paramDef || !paramDef.customEnum) return undefined;
    // row.value may be a number, but customEnum keys are strings
    return paramDef.customEnum[String(row.value)];
  };

  return (
    <Paper className={classes.root}>

      <div className={classes.header}>
         <div className={classes.parametersHeader}>
          <Typography
            variant="h6"
            className={classes.title}
          >{t`Parameters`}</Typography>
          <Tooltip title={t`You can either add or remove parameters from the list below`}>
            <IconButton
              disableRipple
              className={clsx(
                classes.iconBtnStyle,
                classes.addIcon,
                someShowed && classes.pushedAddIcon
              )}
              onClick={(e: any) => openParamsList(e)}
              id="open-unit-params"
            >
              <Add disabled={isLoading} />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.autoUpdateContainer}>
          <Tooltip arrow interactive placement="top-start" title={disableUpdateBtn ? t`Auto update is only available for periods shorter than 24 hours` : t`Refresh data automatically with new real time data`} >
            <FormControlLabel
              classes={{ label: classes.autoRefreshLabel, root: classes.autoUpdate }}
              control={
                <Checkbox
                  color="default"
                  classes={{ root: clsx(classes.removePaddingRight, classes.grayCheckbox) }}
                  checked={autoupdate}
                  disabled={disableUpdateBtn}
                  onChange={() => setAutoupdate(!autoupdate)}
                  value="checkedA"
                />
              }
              label="AUTO UPDATE"
            />
          </Tooltip>
          <Typography variant="body2" className={classes.lastUpdatedTitle}>
            {updateTime
              ? t`Last updated at:` + ` ${moment(updateTime).tz(timezone).format(dateTime)}`
              : t`No` + " " + typeTitle + " " + t`data`}
          </Typography>
          <IconButton disableRipple className={classes.iconBtnStyle} aria-label="refresh icon" onClick={onRefresh ?? _.noop} disabled={autoupdate || disableUpdateBtn}>
            <SvgRefresh className={isLoading ? styles.refreshStyleLoading : styles.refreshStyle} />
          </IconButton>
        </div>
      </div>

      {!isLoading && (<Popover
        id="unitParamsListPopup"
        disableAutoFocus={false}
        disableEnforceFocus={false}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <div className={classes.dialogTitle}>
          <Typography>{paramsTitle}</Typography>
          <IconButton disableRipple className={classes.iconBtnStyle} onClick={handleClose}>
            <Clear />
          </IconButton>
        </div>

        {numOfParams ? <List className={classes.paramsPopupList} >
          <ListItem  key={`param-show-all`} disableGutters dense button onClick={(event: any) => showHideAllParams(event.target.checked)}>
            <Checkbox
              color="default"
              checked={allShowed}
              classes={{ root: classes.grayCheckbox }}
            />
            <ListItemText id={`checkbox-text-all`} primary={t`All`} />
          </ListItem>

          {_.orderBy(Object.values(paramsTable), ["name"], ["asc"]).map((row: any, index: number) => {
            if (!row) {
              return;
            }
            const isSelected = row.isShowing;
            return (
              <ListItem key={`param-show-${index}`} disableGutters dense button onClick={(event: any) => showHideParam(row.code)}>
                <Checkbox
                  color="default"
                  checked={isSelected}
                  classes={{ root: classes.grayCheckbox }}
                />
                <ListItemText id={`checkbox-text-${index}`} primary={row.name} />
              </ListItem>

            );
          })}
        </List>
          : <Typography style={{ padding: "20px 15px", fontSize: "18px" }}>{t`No parameters for this unit`}</Typography>
        }
      </Popover>)}
      <div className={classes.tableWrapper}>
        <Table stickyHeader aria-label="sticky table" className={classes.table}>
          <colgroup>
            <col style={{ width: "20px" }} />
            <col style={{ width: "fit-content" }} />
            <col style={{ width: "43px" }} />
            <col style={{ width: "3rem" }} />
            <col style={{ width: "auto", minWidth: "10rem" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                className={classes.tableCell}
                align={"left"}
                classes={{ stickyHeader: classes.tableHeadStyle }}
              >
                {``}
              </TableCell>
              <TableCell
                className={classes.tableCellAlignLeft}
                align={"left"}
                classes={{ stickyHeader: classes.tableHeadStyle }}
              >
                {t`Name`}
              </TableCell>

              <TableCell
                className={classes.tableCell}
                align={"left"}
                classes={{ stickyHeader: classes.tableHeadStyle }}
              >
                {t`Value`}
              </TableCell>

              <TableCell
                className={classes.tableCell}
                align={"left"}
                classes={{ stickyHeader: classes.tableHeadStyle }}
              >
                {``}
              </TableCell>

              <TableCell
                className={classes.tableCell}
                align={"left"}
                classes={{ stickyHeader: classes.tableHeadStyle }}
              >
                {t`Value range`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!!editedRow && (
              renderEditDialog ? renderEditDialog(editedRow, closeDialog) : (
                <EditParamPopUp
                  row={editedRow}
                  onClose={closeDialog}
                  unitParamTemplate={unitParamTemplate}
                  bacnetUnit={bacnetUnit}
                />
              )
            )}
                        
            {!isLoading && showingParams.map((row: any, index: number) => {
              const {
                name,
                displayValue,
                code,
                slider,
                measurementUnits,
                isChecked,
                disabled,
                plottable,
                value,
                isShowing,
                editable,
              } = row;
              if (!isShowing) {
                return null;
              }
              return (
                <TableRow hover role="checkbox" tabIndex={-1} key={`graphData-${index}`}>
                  <Tooltip
                    arrow
                    title={t`Show parameter on graph`}
                    placement="top-start"
                  >
                    <TableCell
                      className={classes.tableCell}
                      style={{
                        paddingLeft: "auto", minWidth: "auto", backgroundColor: "white"
                      }}
                    >
                      {
                        plottable ? (
                          <Checkbox
                            color="default"
                            checked={isChecked}
                            disabled={disabled}
                            classes={{ root: classes.grayCheckbox }}
                            onClick={() => updateParamRow(code)}
                          />
                        ) : (
                          <span />
                        )
                      }
                    </TableCell>
                  </Tooltip>
                  <Tooltip
                    arrow
                    title={`${name}`}
                    placement="top-start"
                    classes={{ popper: classes.popper }}
                  >
                    <TableCell
                      style={{ backgroundColor: "white" }}
                      className={
                        clsx(classes.tableCellAlignLeft, classes.nameCell)}
                    >
                      {name}

                    </TableCell>
                  </Tooltip>

                  <TableCell
                    className={classes.tableCell}
                    style={{
                      paddingLeft: "auto", minWidth: "auto", backgroundColor: "white"
                    }}
                  >
                    <div className={classes.tableCellValue}>
                        <div className={classes.editContainer}>
                          {editable && (
                            <IconButton onClick={() => editParam(row)}>
                              <EditIcon />
                            </IconButton>
                          )}
                        </div>
                        <Typography>
                           {getCustomEnumValue(row, unitParamTemplate) ?? formatValue(displayValue ?? value)}
                        </Typography>
                      </div>
                  </TableCell>

                  <TableCell
                    className={classes.tableCellAlignLeft}
                    style={{
                      paddingLeft: "15px", minWidth: "auto", backgroundColor: "white"
                    }}
                  >
                    {measurementUnits}
                  </TableCell>

                  <TableCell
                    className={classes.tableCell}
                    style={{
                      paddingLeft: "auto", minWidth: "200px", backgroundColor: "white"
                    }}
                  >
                    {slider}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

    </Paper >
  );

}
